import { TEXT_ON_PATH_MAX_CAPACITY, TEXT_ON_PATH_MIN_STEP_PT } from './constants/capacity-constants'

/**
 * Approximate number of characters that fit along a path: the path's length divided by what one
 * character takes up, which is its average advance plus the letter spacing that tracking and
 * kerning add after every glyph.
 *
 * Returns `null` when there is nothing to divide by (path not measured yet, as in jsdom or before
 * the preview has laid out) so the caller can fall back to its default budget rather than show 0.
 * An estimate, not a layout: real text follows the path's curvature and varies glyph to glyph.
 */
export function estimateTextOnPathCapacity({
  pathLength,
  averageAdvancePt,
  letterSpacingPt,
}: {
  /** Total length of the path the text rides, in pt. */
  pathLength: number
  /** Average glyph advance at the current font and size, in pt, before letter spacing. */
  averageAdvancePt: number
  /** Letter spacing added after every glyph, in pt. Negative when tracking is tightened. */
  letterSpacingPt: number
}): number | null {
  if (!Number.isFinite(pathLength) || pathLength <= 0) return null
  if (!Number.isFinite(averageAdvancePt) || !Number.isFinite(letterSpacingPt)) return null

  const stepPt = Math.max(TEXT_ON_PATH_MIN_STEP_PT, averageAdvancePt + letterSpacingPt)

  return Math.min(TEXT_ON_PATH_MAX_CAPACITY, Math.floor(pathLength / stepPt))
}
